import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import ContactNote from 'types/ContactNote';
import { GlobalState } from 'reducers/stateTypes';
import { setCnError, setNoteDisplayDialog, toggleContactNoteDrawer } from 'reducers/contactNoteListReducer';
import { User } from 'types/User';
import { Box, Button } from '@mui/material';
import { JBColors, JBFooter } from '@juliusbaer-gcrm/gcrm-frontend-components';
import ContactNoteDetailsGeneral from 'views/ContactNoteDetails/Components/ContactNoteDetailsGeneral';
import { DisplayDialogs } from 'views/ContactNotes/ContactNotesContainer';
import { ContactNoteStatus } from 'views/ContactNoteForm/data/statusOptions';
import { EnvConfig } from 'config/envConfig';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import contactNoteDialogService from 'services/contactNoteDialogService';
import { initContactNotes } from 'reducers/contactNoteReducer';
import { ContactNoteDialogStatus } from 'views/ContactNoteFormDialog/zafire/components/data/ContactNoteDialogStatus';

import ContactNoteDetailsEditHistory from './Components/ContactNoteDetailsEditHistory';
import { openModal } from '../../reducers/modalReducer';
import { DeleteNoteDialog } from './zafire/DeleteNoteDialog';

const ContactNoteDetails = () => {
  const dispatch = useDispatch();
  const user: User = useSelector((state: GlobalState) => state.user.userDetails);
  const contactNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const { t } = useTranslation();
  const usedInBr = useSelector(selectIsUsedInBr);
  const dateRange: any[] = useSelector((state: GlobalState) => state.contactNoteList.dateRange);
  const subjectSearch: string = useSelector((state: GlobalState) => state.contactNoteList.subjectSearch);

  const { techId } = useParams<{ techId: string }>();
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteNote = useCallback(async () => {
    if (contactNote?.id) {
      try {
        setIsDeleting(true);
        await contactNoteDialogService.deleteContactNote(contactNote?.id);
        const notes = await contactNoteDialogService.getMyNotes(
          '',
          false,
          subjectSearch,
          dateRange,
          '',
          techId,
          usedInBr,
        );
        dispatch(initContactNotes(notes));
        dispatch(toggleContactNoteDrawer(false));
        setShowDialog(false);
      } catch (error: any) {
        if (error && error.response && error.response.status === 404) {
          dispatch(setCnError(t('contactNote.search.noContactNote')));
        } else {
          dispatch(
            openModal({
              modalType: 'ErrorDialog',
              modalProps: { error },
            }),
          );
        }
        setShowDialog(false);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [contactNote?.id, dateRange, techId, usedInBr, initContactNotes]);

  const renderRightButton = useMemo(() => {
    if (EnvConfig.config.createContactNoteNewUiEnabled) {
      if (contactNote?.status === ContactNoteDialogStatus.SUBMITTED && !user.hasUpdateQualifiedContact) {
        return (
          <Button
            id="contact-note-details-close-button"
            size="small"
            variant="outlined"
            onClick={() => dispatch(toggleContactNoteDrawer(false))}
          >
            {t('noteform.footer.close', 'Close')}
          </Button>
        );
      }
      return (
        <>
          <Button
            id="contact-note-details-close-button"
            size="small"
            variant="outlined"
            onClick={() => dispatch(toggleContactNoteDrawer(false))}
          >
            {t('noteform.footer.close', 'Close')}
          </Button>
          <Button
            variant="contained"
            size="small"
            id="contact-note-details-edit-button"
            onClickCapture={() =>
              user.hasNoteEditAccess || user.hasUpdateQualifiedContact
                ? dispatch(setNoteDisplayDialog(DisplayDialogs.EDIT_VIEW))
                : {}
            }
            disabled={
              user.hasUpdateQualifiedContact
                ? false
                : (contactNote as ContactNote)?.status === ContactNoteStatus.SUBMITTED || !user.hasNoteEditAccess
            }
          >
            {t('noteform.footer.edit', 'Edit')}
          </Button>
        </>
      );
    } else {
      return (
        <Button
          variant="contained"
          size="small"
          id="contact-note-details-edit-button"
          onClickCapture={() =>
            user.hasNoteEditAccess ? dispatch(setNoteDisplayDialog(DisplayDialogs.EDIT_VIEW)) : {}
          }
          disabled={contactNote?.status === ContactNoteStatus.SUBMITTED || !user.hasNoteEditAccess}
        >
          {t('noteform.footer.edit', 'Edit')}
        </Button>
      );
    }
  }, [
    user.hasNoteDeleteAccess,
    user.hasUpdateQualifiedContact,
    user.hasNoteEditAccess,
    contactNote?.status,
    EnvConfig.config.createContactNoteNewUiEnabled,
  ]);

  const renderLeftButton = useMemo(() => {
    if (EnvConfig.config.createContactNoteNewUiEnabled) {
      return (
        (user.hasNoteDeleteAccess || contactNote?.status === ContactNoteDialogStatus.DRAFT) && (
          <Button
            id="contact-note-details-delete-button"
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setShowDialog(true)}
          >
            {t('noteform.footer.delete', 'Delete')}
          </Button>
        )
      );
    } else {
      return (
        <Button
          id="contact-note-details-cancel-button"
          size="small"
          variant="outlined"
          onClick={() => dispatch(toggleContactNoteDrawer(false))}
        >
          {t('noteform.footer.cancel', 'Cancel')}
        </Button>
      );
    }
  }, [user.hasNoteDeleteAccess, contactNote?.status, EnvConfig.config.createContactNoteNewUiEnabled]);

  return (
    <Box>
      {contactNote && (
        <>
          <Box style={{ backgroundColor: JBColors.support.greenSmoke[10] }}>
            <Box mt={0.5} ml={2} maxWidth={880}>
              {!EnvConfig.config.auditApiEnabled && (
                <Box mb={2}>
                  <ContactNoteDetailsEditHistory contactNote={contactNote} />
                </Box>
              )}

              <ContactNoteDetailsGeneral contactNote={contactNote} />
            </Box>
          </Box>

          <JBFooter splitLayout rightButton={renderRightButton} leftButton={renderLeftButton} />
          {EnvConfig.config.createContactNoteNewUiEnabled && (
            <DeleteNoteDialog
              show={showDialog}
              onClose={() => setShowDialog(false)}
              onProceed={() => {
                handleDeleteNote();
              }}
              loading={isDeleting}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ContactNoteDetails;
